import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { or } from '@ember/object/computed';
import { task } from 'ember-concurrency';
import { BeeTemplate } from 'cio/data/bee-templates';
import EditorService, { Editor } from 'cio/services/editor';
import TemplateModel from 'cio/models/template';
import LayoutModel from 'cio/models/layout';
import { ComposerTransactionMessageTemplate } from 'cio/utils/composer/composer-editable-entity';
import { ComposerSection } from 'cio/components/action-composer';
import { tracked } from '@glimmer/tracking';

type ComposerActionTypeName = 'email/drag-and-drop' | 'email/text-and-code' | 'email/choose-template' | 'push';

export default class ComposerTransactionalTemplateController extends Controller {
  queryParams = [
    {
      currentTab: { as: 'tab' },
    },
  ];

  @service editor!: EditorService;

  editableEntity!: ComposerTransactionMessageTemplate;

  composerType: ComposerActionTypeName | null = null;
  selectedEditor: Editor | null = null;

  @tracked isComposerDirty = false;
  @tracked currentTab = ComposerSection.Editor;

  get isDirty() {
    return this.editableEntity.template.get('hasDirtyAttributes') || this.isComposerDirty;
  }

  lastActiveTransactionalRoute = '';

  @or('editableEntity.template.editor', 'selectedEditor') editorName!: Editor;

  @computed('editableEntity.message.state')
  get doneLink() {
    if (this.editableEntity.message.isDraft) {
      return { text: 'Setup', prefix: 'Continue with' };
    } else {
      return { text: 'Overview', prefix: 'Back to' };
    }
  }

  get doneRoute() {
    if (this.lastActiveTransactionalRoute) {
      return this.lastActiveTransactionalRoute;
    } else {
      return 'transactional.message';
    }
  }

  @computed('editorName')
  get isActionComposerV2() {
    return this.editorName === Editor.VISUAL;
  }

  @action
  save() {
    this.editableEntity.template.set('editor', this.editorName);
    return this.editableEntity.template.save();
  }

  didSelectTemplateTask = task(async (template: TemplateModel | BeeTemplate) => {
    if (template instanceof BeeTemplate) {
      // Bee templates' JSON and HTML is fetched async, on demand.
      await template.fetchContent();
    } else {
      this.editableEntity.template.set('bodyAmp', template.bodyAmp);
    }

    let { body, bodyJson, layout, editor } = template;

    this.editableEntity.template.setProperties({
      body,
      bodyJson,
      layout: await layout, // "template.layout" is an async relationship, may need to be awaited
      editor,
    });

    this.set('selectedEditor', editor);
    this.setEditor();
  });

  @action
  didSelectLayout(layout: LayoutModel, editor: Editor) {
    this.editableEntity.template.setProperties({ editor, layout });
    this.set('selectedEditor', editor);
    this.setEditor();
  }

  @action
  startFromScratch(editor: Editor) {
    this.set('selectedEditor', editor);
    this.setEditor();
  }

  setEditor() {
    if (this.editableEntity.isEmail) {
      this.set('composerType', this.editor.getEmailComposerForEditor(this.editorName));
    } else {
      // For all other editor types: push, slack, twilio, etc.
      this.set('composerType', this.editableEntity.message.type);
    }
  }

  @action
  onDirtyChange(isDirty: boolean) {
    this.isComposerDirty = isDirty;
  }
}
